import React, { useState, useEffect, useRef, useCallback } from 'react';
import { SignalRService } from '../services/signalRService';
import { ApiService } from '../services/apiService';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import { LoadingSpinner } from './LoadingSpinner';
import { ErrorBanner } from './ErrorBanner';

export const ChatInterface = ({ 
  apiBaseUrl = 'http://localhost:5272', 
  userId = 'demo-user',
  onClose,
  isModal = false
}) => {
  const [messages, setMessages] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  
  const signalRService = useRef();
  const apiService = useRef();
  const typingTimeoutRef = useRef();
  const currentSessionIdRef = useRef('');

  useEffect(() => {
    console.log('=== CHAT INTERFACE MOUNTED ===');
    console.log('apiBaseUrl:', apiBaseUrl);
    console.log('userId:', userId);
    console.log('Environment variables:');
    console.log('REACT_APP_API_BASE_URL:', process.env.REACT_APP_API_BASE_URL);
    console.log('REACT_APP_USER_ID:', process.env.REACT_APP_USER_ID);
    
    initializeServices();
    return () => {
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializeServices = async () => {
    try {
      console.log('=== INITIALIZING SERVICES ===');
      setIsConnecting(true);
      
      // Initialize services
      console.log('Creating SignalR and API services...');
      signalRService.current = new SignalRService(apiBaseUrl);
      apiService.current = new ApiService(apiBaseUrl);
      console.log('Services created, API Base URL:', apiBaseUrl);
      
      // Setup SignalR event handlers
      console.log('Setting up SignalR event handlers...');
      setupSignalREventHandlers();

      // Start SignalR connection
      console.log('Starting SignalR connection...');
      await signalRService.current.start();
      console.log('SignalR connection started successfully');
      setIsConnected(true);
      setError(''); // Clear any existing errors on successful connection

      // Create or get a session
      console.log('Creating or joining session...');
      await createOrJoinSession();
      console.log('Session setup completed');
      
    } catch (error) {
      console.error('Failed to initialize services:', error);
      setError('No se pudo conectar al servicio de chat. Por favor, actualiza la página e intenta de nuevo.');
    } finally {
      console.log('Initialization finished, setting isConnecting to false');
      setIsConnecting(false);
    }
  };

  const setupSignalREventHandlers = () => {
    if (!signalRService.current) return;

    signalRService.current.onSessionJoined((data) => {
      console.log('SessionJoined event received:', data);
      setMessages(data.messages || data.Messages || []);
      setCurrentSessionId(data.sessionId || data.SessionId);
      currentSessionIdRef.current = data.sessionId || data.SessionId;
    });

    signalRService.current.onMessageReceived((message) => {
      console.log('Message received from server:', message);
      setMessages(prev => [...prev, message]);
      
      // Stop processing when assistant responds
      if (message.role === 'assistant') {
        setIsProcessing(false);
        setIsTyping(false);
      }
    });

    signalRService.current.onMessageSent((confirmation) => {
      console.log('Message sent confirmation:', confirmation);
    });

    signalRService.current.onError((message) => {
      setError(message);
      setTimeout(() => setError(''), 5000);
    });

    // Handle reconnection events
    signalRService.current.onReconnecting((error) => {
      setIsConnected(false);
      setError('Reconectando al servicio de chat...');
    });

    signalRService.current.onReconnected((connectionId) => {
      setIsConnected(true);
      setError(''); // Clear any error messages on successful reconnection
      console.log('Successfully reconnected to chat service');
      
      // Rejoin the session if we have one
      if (currentSessionIdRef.current) {
        signalRService.current.joinSession(currentSessionIdRef.current).catch(error => {
          console.error('Failed to rejoin session after reconnection:', error);
          setError('Reconectado, pero no se pudo unir a la sesión. Intenta enviar un mensaje.');
        });
      }
    });

    signalRService.current.onConnectionClosed((error, maxAttemptsReached) => {
      setIsConnected(false);
      if (maxAttemptsReached) {
        setError('No se pudo reconectar al servicio de chat. Por favor, actualiza la página.');
      } else {
        setError('Se perdió la conexión con el servicio de chat. Intentando reconectar...');
      }
    });
  };

  const createOrJoinSession = async () => {
    try {
      if (!apiService.current) return;

      // Check if we have a stored session ID
      const storedSessionId = localStorage.getItem('facturas_chat_session_id');
      console.log('Stored session ID:', storedSessionId);

      if (storedSessionId) {
        // Try to rejoin existing session
        console.log('Attempting to rejoin stored session:', storedSessionId);
        try {
          const response = await apiService.current.getSession(storedSessionId);
          if (response.success && response.data) {
            setCurrentSessionId(storedSessionId);
            currentSessionIdRef.current = storedSessionId;
            setMessages(response.data.messages || []);
            
            if (signalRService.current) {
              await signalRService.current.joinSession(storedSessionId);
              console.log('Successfully rejoined stored session');
              return;
            }
          }
        } catch (error) {
          console.log('Failed to rejoin stored session, creating new one:', error);
          // Clear invalid session ID
          localStorage.removeItem('facturas_chat_session_id');
        }
      }

      // Create new session
      console.log('Creating new session...');
      const response = await apiService.current.createSession(userId, 'Nueva Consulta');
      console.log('Create session response:', response);
      
      if (response.success && response.data) {
        const sessionId = response.data.id;
        console.log('New session created:', sessionId);
        
        setCurrentSessionId(sessionId);
        currentSessionIdRef.current = sessionId;
        
        // Store in localStorage
        localStorage.setItem('facturas_chat_session_id', sessionId);
        console.log('Session ID stored in localStorage');
        
        if (signalRService.current) {
          await signalRService.current.joinSession(sessionId);
          console.log('Successfully joined new session');
        }
      } else {
        throw new Error(response.message || 'Failed to create session');
      }
    } catch (error) {
      console.error('Failed to create/join session:', error);
      setError('No se pudo crear la sesión de chat');
    }
  };

  const sendMessage = useCallback(async (message) => {
    console.log('=== DEBUG: sendMessage called ===');
    console.log('message:', message);
    console.log('signalRService.current exists:', !!signalRService.current);
    console.log('currentSessionId:', currentSessionId);
    console.log('isProcessing:', isProcessing);
    console.log('isConnected:', isConnected);
    
    if (!signalRService.current || !currentSessionId || !message.trim() || isProcessing) {
      console.log('=== MESSAGE BLOCKED ===');
      console.log('- signalRService missing:', !signalRService.current);
      console.log('- sessionId missing:', !currentSessionId);
      console.log('- message empty:', !message.trim());
      console.log('- already processing:', isProcessing);
      return;
    }

    try {
      console.log('=== SENDING MESSAGE ===');
      setIsProcessing(true);
      
      // Add user message to UI immediately
      const userMessage = {
        id: `temp-${Date.now()}`,
        sessionId: currentSessionId,
        role: 'user',
        content: message,
        timestamp: new Date().toISOString(),
      };
      
      console.log('Adding user message to UI:', userMessage);
      setMessages(prev => [...prev, userMessage]);
      setIsTyping(true);
      
      // Send via SignalR
      console.log('Calling SignalR sendMessage...');
      await signalRService.current.sendMessage(currentSessionId, message);
      console.log('SignalR sendMessage completed');
      
    } catch (error) {
      console.error('Failed to send message:', error);
      setError('No se pudo enviar el mensaje. Por favor, intenta de nuevo.');
      setIsTyping(false);
    } finally {
      console.log('Setting isProcessing to false');
      setIsProcessing(false);
    }
  }, [currentSessionId, isProcessing, isConnected]);

  const cleanup = () => {
    console.log('Cleaning up chat interface...');
    
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    if (signalRService.current) {
      signalRService.current.removeAllListeners();
      signalRService.current.stop();
    }

    // Optionally clear session from localStorage on cleanup
    // localStorage.removeItem('chat_session_id');
  };

  if (isConnecting) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50">
        <div className="text-center">
          <LoadingSpinner size="large" />
          <p className="mt-4 text-gray-600">Conectando al Chat de Facturas Simples...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Error Banner */}
      {error && (
        <ErrorBanner message={error} onClose={() => setError('')} />
      )}
      
      {/* Messages */}
        <MessageList 
          messages={messages}
          isTyping={isTyping}
          apiBaseUrl={apiBaseUrl}
        />      {/* Message Input */}
      <MessageInput
        onSendMessage={sendMessage}
        disabled={!isConnected}
        isProcessing={isProcessing}
      />
    </div>
  );
};
